/**
 * @lines98/client/lib/run-resume — «Играть» → сразу в открытую партию, иначе лобби
 * (run-persistence-lifecycle.md T-B / I-4 / §5.2c).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-directive 2026-06-13): раньше «Играть» ВСЕГДА вело в
 *   лобби выбора режима. T-B: если в кэше есть АКТИВНАЯ незавершённая не-daily партия —
 *   игрок попадает СРАЗУ в неё (доска возобновлена локальным реплеем seed+moves), а не
 *   в лобби. Это ОДИН модуль (НЕ копия по экранам — code-structure §1/§3), которым
 *   пользуется и nav-rail «Играть», и boot-вход (app.ts).
 *
 * КАК (addendum Решение 2 «контракт возобновления»): возобновление — ЧИСТО клиентское.
 *   1. читаем запись кэша (`lib/in-progress-cache.loadRun`);
 *   2. монтируем партию в PLAYING (preStart=false) на кэшированном seed;
 *   3. реплеим moves через `attemptMove` под `replaying`-гардом (детерминизм движка:
 *      pre-replay state === post-replay state) — ровно как daily `resumeInProgress`;
 *   4. финальный submit (на завершении) — обычный finish-game с тем же sessionId
 *      (all_time) / локальный record (casual). Сервер при resume не нужен (кроме
 *      heartbeat-продления TTL — RP-5).
 *
 * SCOPE: ветвление «Играть» (resume-vs-lobby) + локальный реплей восстановления.
 *   «Пустое превью (0 ходов) ≠ активная партия» — кэш пишется только с первого хода
 *   (P1-2: дефолт НЕТ ходов → лобби). При нескольких партиях — самая недавняя по
 *   `startedAt` (P1-1) — но кэш держит ОДНУ активную не-daily партию, так что выбор
 *   тривиален. Daily — своя поверхность (свой вход), не конфликтует (§9 Edge).
 * NOT IN SCOPE: heartbeat / TTL-«партия истекла» (RP-5 — добавит probe + тост);
 *   daily resume (daily-board-screen); finish-game submit (game-submit).
 *
 * Cross-references:
 *   - run-persistence-lifecycle.md §5 (T-B) + §8.3 (resume-вход) + addendum Решение 2.
 *   - lib/in-progress-cache.ts (RP-3 — источник записи активной партии).
 *   - screens/daily-board-screen.ts resumeInProgress (sibling — daily local replay).
 *   - store/game-store.ts (startAllTimeGame / startNewGame + setReplaying гард).
 */

import { heartbeatOnResume } from './game-session-heartbeat.js';
import { loadRun, type InProgressRun } from './in-progress-cache.js';
import { useGameStore } from '../store/game-store.js';

/**
 * «Играть» entry branch (I-4): if an ACTIVE unfinished non-daily run is cached →
 * resume it directly into the board; otherwise → the mode-selector lobby.
 *
 * «Active» = a cached run with ≥1 recorded move (the cache is written only from the
 * first valid move — an empty preview is NOT active, P1-2 → lobby). Async because the
 * cache read is async; callers fire-and-forget (the store transition drives the UI).
 */
export async function resumeOrLobby(): Promise<void> {
  const run = await loadRun();
  if (run && run.moves.length > 0) {
    resumeRun(run);
    return;
  }
  // No active run (or an empty/0-move record) → the lobby (P1-2 default).
  useGameStore.getState().showModeSelect();
}

/**
 * Resume a cached non-daily run: mount the game in PLAYING state on the cached seed,
 * then replay each cached move under the `replaying` guard (so the recovered state
 * paints STATICALLY — no spawn-pop / line-clear re-enactment, mirrors daily
 * `resumeInProgress`). Deterministic: same seed + same moves → same state.
 *
 * all_time → `startAllTimeGame` with the cached `sessionId` (the SAME server session
 * is continued; finish-game verifies it). casual → `startNewGame` with the cached
 * mode (purely local). Exported for the boot path + tests.
 */
export function resumeRun(run: InProgressRun): void {
  const store = useGameStore.getState();
  if (run.kind === 'all_time') {
    store.startAllTimeGame({
      seed: run.seed,
      sessionId: run.sessionId,
      mode: run.mode,
      preStart: false, // resume into PLAYING — scoring already begun
    });
  } else {
    store.startNewGame(run.seed, run.mode);
  }

  // Guard ON before the first replayed move grows `moves` under the board-screen
  // subscription (set AFTER the mount, which resets `replaying` to false).
  useGameStore.getState().setReplaying(true);
  try {
    for (const m of run.moves) {
      useGameStore.getState().selectCell(m.sr, m.sc);
      const res = useGameStore.getState().attemptMove(m.r, m.c);
      if (!res.ok) {
        // Cache corruption / engine drift — remaining moves won't replay correctly.
        // Conservative: restart fresh on the same seed/mode (re-arm the static guard;
        // the `finally` clears it). The run cache is overwritten on the next move.
        if (run.kind === 'all_time') {
          useGameStore.getState().startAllTimeGame({
            seed: run.seed,
            sessionId: run.sessionId,
            mode: run.mode,
            preStart: false,
          });
        } else {
          useGameStore.getState().startNewGame(run.seed, run.mode);
        }
        useGameStore.getState().setReplaying(true);
        break;
      }
    }
  } finally {
    useGameStore.getState().setReplaying(false);
  }

  // RP-5: on resume, ping the server to SLIDE the session TTL immediately (not
  // throttled — it must land before play continues, to extend a possibly-near-expiry
  // session). all_time only (casual has no server session). If the server says the
  // session is gone (TTL elapsed while the tab was closed), the heartbeat module
  // surfaces «партия истекла» + clears the cache + routes to the lobby (AC-B.6) — no
  // silent seed substitution (I-1). Fire-and-forget — the local replay above already
  // restored the board; the ping only adjudicates server-side liveness.
  if (run.kind === 'all_time') {
    void heartbeatOnResume(run.sessionId);
  }

  // store.phase is already 'board' (set by the mount); app.ts mounts the board on the
  // next subscribe-fire. No explicit showBoard needed.
}
